import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StatCard, PageHeader, StatusBadge } from "@/components/app/PageBits";
import { fmtINR, fmtDate, CYCLE_LABEL } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { LogOut, Sprout, RefreshCcw, Download, FileDown } from "lucide-react";
import { toast } from "sonner";
import { downloadInvoicePdf, downloadReceiptPdf, parseBlobError, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function CustomerPortal() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [plots, setPlots] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqPlot, setReqPlot] = useState(null);
  const [toPlan, setToPlan] = useState("regular");

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && user && user.role !== "customer") navigate("/dashboard");
  }, [user, loading, navigate]);

  const load = async () => {
    const [p, i, s, pay] = await Promise.all([
      api.get("/me/plots"), api.get("/invoices"), api.get("/me/summary"), api.get("/me/payments"),
    ]);
    setPlots(p.data); setInvoices(i.data); setSummary(s.data); setPayments(pay.data);
  };
  useEffect(() => { if (user?.role === "customer") load(); }, [user]);

  const submitReq = async () => {
    try { await api.post("/plan-change-requests", { plot_id: reqPlot.id, to_plan: toPlan }); toast.success("Request submitted"); setReqOpen(false); }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const oneYearAgo = new Date(Date.now() - 365 * 86400 * 1000).toISOString().slice(0, 10);
  const [rangeFrom, setRangeFrom] = useState(oneYearAgo);
  const [rangeTo, setRangeTo] = useState(today);
  const downloadStatement = async () => {
    try {
      const r = await api.get(`/me/statement.pdf?from=${rangeFrom}&to=${rangeTo}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `statement_${rangeFrom}_${rangeTo}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Statement downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading || !user) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-background grain">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 max-md:px-4 py-4 max-md:py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center"><Sprout className="h-4 w-4" strokeWidth={1.5}/></div>
            <div><div className="font-heading font-semibold text-sm">Sanjeevani Estates</div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Customer portal</div></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right text-xs hidden sm:block"><div className="font-medium">{user.name}</div><div className="text-muted-foreground">{user.email}</div></div>
            <Button data-testid="portal-logout" variant="outline" size="sm" onClick={logout}><LogOut className="h-4 w-4"/></Button>
          </div>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-6 py-10 max-md:px-4 max-md:py-6">
        <PageHeader title={`Welcome, ${user.name?.split(" ")[0]}`} subtitle="Your plots, plans and invoices — updated in real time."/>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-md:gap-3 mb-10">
          <StatCard testId="c-billed" label="Total billed" value={fmtINR(summary?.billed)}/>
          <StatCard testId="c-paid" label="Total paid" value={fmtINR(summary?.paid)} tone="good"/>
          <StatCard testId="c-outstanding" label="Outstanding" value={fmtINR(summary?.outstanding)} tone="bad"/>
          <StatCard testId="c-credit" label="Credit balance" value={fmtINR(summary?.credit_balance)} tone="warn"/>
        </div>

        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold tracking-tight">My plots</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-12">
          {plots.map((p) => (
            <div key={p.id} data-testid={`my-plot-${p.id}`} className="border border-border rounded-md bg-card p-5 hover:-translate-y-0.5 hover:shadow-sm transition-transform">
              <div className="flex items-center justify-between">
                <div className="font-heading font-bold text-lg">{p.plot_no}</div>
                <StatusBadge status={p.status}/>
              </div>
              <div className="text-xs text-muted-foreground">{p.project_name}</div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                <div><div className="text-xs text-muted-foreground">Area</div><div className="tabular font-medium">{p.area_sqft.toLocaleString("en-IN")} sqft</div></div>
                <div><div className="text-xs text-muted-foreground">Plan</div><div className="capitalize font-medium">{p.maintenance_plan.replace("_"," ")}</div></div>
                <div><div className="text-xs text-muted-foreground">Cycle</div><div className="font-medium">{CYCLE_LABEL[p.billing_cycle]}</div></div>
                <div><div className="text-xs text-muted-foreground">Next cycle</div><div className="tabular font-medium">{fmtDate(p.next_cycle_start)}</div></div>
              </div>
              <Button data-testid={`req-plan-${p.id}`} size="sm" variant="outline" className="mt-5 w-full" onClick={()=>{setReqPlot(p); setToPlan(p.maintenance_plan === "regular" ? "self_managed" : "regular"); setReqOpen(true);}}>
                <RefreshCcw className="h-3 w-3 mr-1"/> Request plan change
              </Button>
            </div>
          ))}
          {plots.length === 0 && <div className="col-span-full text-center text-muted-foreground py-10 border border-dashed border-border rounded-md">No plots linked to your account yet.</div>}
        </div>

        <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
          <h2 className="font-heading text-xl font-bold tracking-tight">Invoices</h2>
          <div className="flex items-end gap-2 max-md:w-full max-md:flex-wrap">
            <div className="max-md:flex-1">
              <Label className="text-xs">From</Label>
              <Input data-testid="stmt-from" type="date" value={rangeFrom} onChange={(e)=>setRangeFrom(e.target.value)} className="h-9 w-40 max-md:w-full"/>
            </div>
            <div className="max-md:flex-1">
              <Label className="text-xs">To</Label>
              <Input data-testid="stmt-to" type="date" value={rangeTo} onChange={(e)=>setRangeTo(e.target.value)} className="h-9 w-40 max-md:w-full"/>
            </div>
            <Button data-testid="stmt-download" variant="outline" onClick={downloadStatement} className="h-9 max-md:w-full max-md:h-11">
              <FileDown className="h-4 w-4 mr-2"/> Statement PDF
            </Button>
          </div>
        </div>
        <div className="border border-border rounded-md bg-card overflow-hidden">
          <table className="w-full text-sm m-cards">
            <thead className="bg-muted/60"><tr className="text-left">
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider">Invoice #</th>
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider">Plot</th>
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider">Period</th>
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider text-right">Amount</th>
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider text-right">Paid</th>
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider text-right">Balance</th>
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider">Status</th>
              <th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id} data-testid={`my-inv-${i.id}`} className="border-t border-border">
                  <td className="px-4 py-3 tabular font-medium">{i.invoice_no}</td>
                  <td className="px-4 py-3">{i.plot_no}</td>
                  <td className="px-4 py-3 tabular text-xs text-muted-foreground">{fmtDate(i.cycle_start)} → {fmtDate(i.cycle_end)}</td>
                  <td className="px-4 py-3 text-right tabular">{fmtINR(i.amount)}</td>
                  <td className="px-4 py-3 text-right tabular text-primary">{fmtINR(i.amount_paid)}</td>
                  <td className="px-4 py-3 text-right tabular text-destructive">{fmtINR(i.balance)}</td>
                  <td className="px-4 py-3"><StatusBadge status={i.status}/></td>
                  <td className="px-4 py-3 text-right">
                    <Button data-testid={`my-inv-pdf-${i.id}`} size="sm" variant="ghost" onClick={async()=>{ try{ await downloadInvoicePdf(i); }catch{ toast.error("PDF failed"); } }} title="Download PDF"><Download className="h-3.5 w-3.5"/></Button>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No invoices yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-10 max-md:px-4 max-md:pb-10">
        <PageHeader title="My payments" subtitle={`${payments.length} payment${payments.length===1?"":"s"} on file`} />
        <div className="border border-border rounded-md bg-card overflow-hidden">
          <table className="w-full text-sm m-cards">
            <thead className="bg-muted/60"><tr className="text-left">
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider">Date</th>
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider">Invoice #</th>
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider">Method</th>
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider">Reference</th>
              <th className="px-4 py-3 text-xs uppercase font-heading tracking-wider text-right">Amount</th>
              <th className="px-4 py-3 text-right text-xs uppercase font-heading tracking-wider">Receipt</th>
            </tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} data-testid={`my-pay-${p.id}`} className="border-t border-border">
                  <td className="px-4 py-3 tabular text-muted-foreground">{fmtDate(p.paid_at || p.created_at)}</td>
                  <td className="px-4 py-3 tabular font-medium">{p.invoice_no || "-"}</td>
                  <td className="px-4 py-3 text-muted-foreground capitalize">{(p.method || "").replace("_"," ")}</td>
                  <td className="px-4 py-3 tabular text-muted-foreground">{p.reference_no || "-"}</td>
                  <td className="px-4 py-3 text-right tabular text-primary">{fmtINR(p.amount)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button data-testid={`my-pay-receipt-${p.id}`} size="sm" variant="outline"
                            onClick={async()=>{ try{ await downloadReceiptPdf(p); }catch(e){ toast.error(await parseBlobError(e)); } }}>
                      <FileDown className="h-3.5 w-3.5 mr-1.5"/>Download
                    </Button>
                  </td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No payments on file.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request plan change · {reqPlot?.plot_no}</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">Plan changes are allowed only in the 7-day window right after a cycle completes. Your admin will review and approve.</p>
            <div>
              <label className="text-sm font-medium">Switch to</label>
              <Select value={toPlan} onValueChange={setToPlan}>
                <SelectTrigger data-testid="req-to-plan"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="self_managed">Self-Managed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button data-testid="req-submit" onClick={submitReq}>Submit request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
